import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDeviceHistory, useRestoreDevice } from '../../utils/reactQueryHooks';
import '../../styles/deviceHistory.css';

const formatKind = (kind) => (kind === 'sensor' ? 'Sensor' : 'Actuator');

export default function DeviceHistory() {
  const [kindFilter, setKindFilter] = useState('all');
  const [restoreError, setRestoreError] = useState('');
  const { data: history = [], isPending, isError, error, refetch } = useDeviceHistory();
  const restoreMutation = useRestoreDevice();

  const filtered = useMemo(() => {
    if (kindFilter === 'all') return history;
    return history.filter((item) => item.deviceKind === kindFilter);
  }, [history, kindFilter]);

  const handleRestore = (item) => {
    setRestoreError('');
    restoreMutation.mutate(item.macAddress, {
      onError: (err) => {
        setRestoreError(err.message || 'Could not reconnect this device.');
      },
    });
  };

  return (
    <div className="device-history-page">
      <header className="device-history-header">
        <div>
          <h1>Device history</h1>
          <p>
            Disconnected sensors and actuators stay here so you can reconnect them later,
            as long as the MAC is not claimed by another account.
          </p>
        </div>
        <Link className="device-history-back" to="/dashboard">Back to dashboard</Link>
      </header>

      <div className="device-history-toolbar">
        <label htmlFor="history-kind-filter">Show</label>
        <select
          id="history-kind-filter"
          value={kindFilter}
          onChange={(e) => setKindFilter(e.target.value)}
        >
          <option value="all">All devices</option>
          <option value="sensor">Sensors</option>
          <option value="actuator">Actuators</option>
        </select>
        <button type="button" className="device-history-refresh" onClick={() => refetch()}>
          Refresh
        </button>
      </div>

      {restoreError && <p className="device-history-error">{restoreError}</p>}
      {isError && <p className="device-history-error">{error?.message || 'Could not load history.'}</p>}

      {isPending ? (
        <p>Loading disconnected devices...</p>
      ) : filtered.length === 0 ? (
        <p className="device-history-empty">No disconnected devices{kindFilter !== 'all' ? ' in this filter' : ''}.</p>
      ) : (
        <div className="device-history-list">
          {filtered.map((item) => (
            <article key={`${item.deviceKind}-${item.macAddress}-${item.historyId}`} className="device-history-card">
              <div className="device-history-card-body">
                <span className={`device-history-kind device-history-kind-${item.deviceKind}`}>
                  {formatKind(item.deviceKind)}
                </span>
                <h2>{item.displayName || item.macAddress}</h2>
                <p className="device-history-mac">{item.macAddress}</p>
                {item.deviceKind === 'actuator' && (
                  <p className="device-history-sensor">
                    Last paired to {item.lastSensorDescription || item.lastSensorMac || 'an unknown sensor'}
                    {item.lastSensorMac && item.lastSensorDescription ? ` (${item.lastSensorMac})` : ''}
                  </p>
                )}
                {item.disconnectedAt && (
                  <p className="device-history-time">Disconnected {item.disconnectedAt}</p>
                )}
              </div>
              <button
                type="button"
                className="green-button device-history-restore"
                onClick={() => handleRestore(item)}
                disabled={restoreMutation.isPending}
              >
                {restoreMutation.isPending ? 'Reconnecting…' : 'Reconnect'}
              </button>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
